/**
 * DraftCard — the copilot approve / edit / reject control, wherever a pending
 * agent draft is shown.
 *
 * Originally the console's Drafts tab owned this. It now also renders on the
 * LEAD PAGE, docked under the conversation, because that is where recruiters
 * already are: the old flow made them read the lead's text on one screen, then
 * leave for the console to release the reply, and the trip was long enough that
 * people started answering manually over the top of a waiting draft.
 *
 * `compact` is the lead-page dress: the same buttons and the same semantics,
 * trimmed of the queue-triage furniture (contact name, keyboard hint) that only
 * makes sense when you are working a list of other people's conversations.
 */
import { useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Pencil, MessageSquareText, ShieldAlert, CalendarClock } from 'lucide-react';
import { fieldCls } from '../filters';
import {
  DRAFT_SLA_MS, btnPrimary, btnGhost, btnDanger, btnDangerGhost, ago, isSchedulerDraft, type DraftRow,
} from './shared';

export default function DraftCard({
  t, busy, nowTs, showContact = false, compact = false, heading, onApprove, onReject, onViewThread,
}: {
  t: DraftRow;
  busy: boolean;
  nowTs: number;
  /** Queue surfaces name the lead; a lead page already has them in the header. */
  showContact?: boolean;
  /** Lead-page dress — tighter, no triage furniture. */
  compact?: boolean;
  /** Overrides the default "Waiting for your approval" title. */
  heading?: string;
  onApprove: (t: DraftRow, text?: string) => Promise<void>;
  onReject: (t: DraftRow, reason: string) => Promise<void>;
  onViewThread?: () => void;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const isSuppressed = t.status === 'suppressed';
  const slaLeft = DRAFT_SLA_MS - (nowTs - new Date(t.created_at).getTime());
  const sla = isSuppressed ? null : (slaLeft <= 0
    ? { text: `SLA overdue ${Math.max(1, Math.floor(-slaLeft / 60000))}m`, overdue: true }
    : { text: `SLA in ${Math.max(1, Math.ceil(slaLeft / 60000))}m`, overdue: false });
  // C-14: V7/V8 live in validator_results.model — including the M-21
  // `no_validator_model` error rows that mean "never ran", which an approver
  // must be able to tell apart from "ran clean".
  const modelResults: any[] = Array.isArray(t.validator_results?.model) ? t.validator_results.model : [];
  const modelIssues = modelResults.filter((r) => !r.ok);
  const failed = (t.validator_results?.deterministic || []).filter((r: any) => !r.ok).map((r: any) => r.id);

  // C-16: one-keystroke approve on a FOCUSED draft card. Never hijacks typing.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return;
    if (editing || rejecting || busy) return;
    if (e.key === 'Enter' || e.key === 'a' || e.key === 'A') {
      e.preventDefault();
      onApprove(t);
    }
  };

  return (
    <div
      tabIndex={0}
      onKeyDown={onKey}
      className={`rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 ${compact ? 'p-2.5' : 'p-4'} ${isSuppressed
        ? 'border-red-200 dark:border-red-900/60'
        : 'border-gray-200 dark:border-gray-700'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
          {showContact && (
            <button
              type="button"
              onClick={() => t.contact_id && navigate(`/crm/contacts/${t.contact_id}`)}
              className="text-sm font-semibold text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
            >
              {t.contact_name || 'Lead'}
            </button>
          )}
          {!showContact && (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <MessageSquareText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              {heading || 'Waiting for your approval'}
            </span>
          )}
          {/* Review M-13: the guardrail-suppressed salvage queue — approving one
              (as-is or edited) is the human taking accountability for copy the
              wall refused. */}
          {isSuppressed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
              <ShieldAlert className="h-3 w-3" /> suppressed — approve only if you take responsibility
            </span>
          )}
          {/* Scheduler mode: this draft is the scheduling assistant's (it
              only books calls; it never discusses the study). */}
          {isSchedulerDraft(t) && (
            <span
              title="Written by the texting agent's scheduling assistant, which only sets up calls"
              className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
            >
              <CalendarClock className="h-3 w-3" /> Scheduler
            </span>
          )}
        </span>
        <span className="inline-flex flex-wrap items-center gap-2">
          {!compact && !isSuppressed && !editing && !rejecting && (
            <span className="hidden items-center gap-1 text-[10px] text-gray-400 sm:inline-flex">
              <kbd className="rounded border border-gray-300 px-1 font-sans dark:border-gray-600">A</kbd>
              <span>/</span>
              <kbd className="rounded border border-gray-300 px-1 font-sans dark:border-gray-600">Enter</kbd>
              <span>approves when focused</span>
            </span>
          )}
          <span className="text-xs text-gray-400">waiting {ago(t.created_at)}</span>
          {sla && (
            <span className={`whitespace-nowrap text-xs ${sla.overdue ? 'font-medium text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
              {sla.text}
            </span>
          )}
          {onViewThread && (
            <button
              type="button"
              onClick={onViewThread}
              title="Open this conversation's full thread in the console"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              <MessageSquareText className="h-3.5 w-3.5" /> View thread
            </button>
          )}
        </span>
      </div>
      {editing ? (
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          rows={3}
          maxLength={320}
          className={`${fieldCls} mt-2 w-full`}
          aria-label="Edit the draft message"
        />
      ) : (
        <p className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-sm text-gray-800 dark:bg-gray-900/40 dark:text-gray-200">
          {t.draft_body}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(t.validator_results?.deterministic || []).map((r: any) => (
          <span
            key={r.id}
            title={r.reason || ''}
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.ok
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}
          >
            {r.id}
          </span>
        ))}
        {/* C-14: the V7/V8 model checks — green ran-clean, red real violation,
            amber "did not run" (error rows, incl. M-21's no_validator_model). */}
        {modelResults.map((r: any) => (
          <span
            key={r.id}
            title={r.error ? `did not run: ${r.error}` : (r.violations || []).join('; ')}
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.ok
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : r.error
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}
          >
            {r.id}{r.error ? ' · not run' : ''}
          </span>
        ))}
        {failed.length > 0 && (
          <span className="text-xs text-red-600 dark:text-red-400">failed: {failed.join(', ')}</span>
        )}
        {modelIssues.length > 0 && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            model checks: {modelIssues.map((r: any) => `${r.id} ${r.error ? `error (${r.error})` : 'violation'}`).join(', ')}
          </span>
        )}
        <span className="ml-auto inline-flex flex-wrap gap-2">
          {editing ? (
            <>
              <button type="button" disabled={busy || !editText.trim()} onClick={() => { onApprove(t, editText); setEditing(false); }} className={btnPrimary}>
                <Check className="h-4 w-4" /> Send edited
              </button>
              <button type="button" onClick={() => setEditing(false)} className={btnGhost}>Cancel</button>
            </>
          ) : rejecting ? null : (
            <>
              <button type="button" disabled={busy} onClick={() => onApprove(t)} className={btnPrimary} title="Send this reply to the lead (after a short natural delay)">
                <Check className="h-4 w-4" /> Approve
              </button>
              <button type="button" onClick={() => { setEditing(true); setEditText(t.draft_body); }} className={btnGhost}>
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button type="button" disabled={busy} onClick={() => { setRejecting(true); setRejectReason(''); }} className={btnDangerGhost} title="Don't send — the agent learns from your reason">
                <X className="h-4 w-4" /> Reject
              </button>
            </>
          )}
        </span>
      </div>
      {rejecting && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why reject? (feeds tuning — optional)"
            aria-label="Rejection reason"
            className={`${fieldCls} min-w-0 flex-1`}
          />
          <button type="button" disabled={busy} onClick={() => { onReject(t, rejectReason); setRejecting(false); }} className={btnDanger}>
            Reject draft
          </button>
          <button type="button" onClick={() => setRejecting(false)} className={btnGhost}>Keep</button>
        </div>
      )}
    </div>
  );
}
